import {
  Avatar,
  Button,
  Card,
  Collapse,
  Result,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  BarChartOutlined,
  LogoutOutlined,
  MessageOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useOrganization } from "@/api/usage";
import { useAuth } from "@/auth/AuthContext";
import { Logo } from "@/components/Logo";
import { PeriodInfo } from "@/components/PeriodInfo";
import { UsageCard } from "@/components/UsageCard";
import type {
  OrganizationResponse,
  PeriodUsage,
  PlatformMetrics,
} from "@/models/usage";

const { Title, Text } = Typography;

const ZERO_METRICS: Required<PlatformMetrics> = { requests: 0, mentions: 0 };

const PLATFORM_PALETTE = [
  "#3B82F6",
  "#22C55E",
  "#F97316",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
  "#EAB308",
  "#EF4444",
  "#6366F1",
  "#10B981",
  "#F59E0B",
  "#06B6D4",
  "#84CC16",
  "#A855F7",
  "#F472B6",
  "#0EA5E9",
  "#FB7185",
  "#64748B",
] as const;

function colorForPlatform(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return PLATFORM_PALETTE[hash % PLATFORM_PALETTE.length];
}

interface PlatformRow {
  readonly key: string;
  readonly platform: string;
  readonly requests: number;
  readonly mentions: number;
}

function splitTotalAndPlatforms(usage: PeriodUsage | null | undefined): {
  total: Required<PlatformMetrics>;
  platforms: PlatformRow[];
} {
  if (!usage) {
    return { total: { ...ZERO_METRICS }, platforms: [] };
  }
  const totalEntry = usage.total ?? ZERO_METRICS;
  const total: Required<PlatformMetrics> = {
    requests: totalEntry.requests ?? 0,
    mentions: totalEntry.mentions ?? 0,
  };
  const platforms: PlatformRow[] = Object.entries(usage)
    .filter(([name]) => name !== "total")
    .map(([name, metrics]) => ({
      key: name,
      platform: name,
      requests: metrics.requests ?? 0,
      mentions: metrics.mentions ?? 0,
    }))
    .sort((a, b) => b.requests - a.requests);
  return { total, platforms };
}

function platformTableColumns(maxRequests: number, maxMentions: number): ColumnsType<PlatformRow> {
  return [
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      render: (value: string) => {
        const color = colorForPlatform(value);
        return (
          <div className="flex items-center gap-2.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: color }}
            />
            <span className="capitalize font-medium text-content-primary">
              {value}
            </span>
          </div>
        );
      },
      sorter: (a, b) => a.platform.localeCompare(b.platform),
    },
    {
      title: "Requests",
      dataIndex: "requests",
      key: "requests",
      width: 260,
      render: (value: number, record) => (
        <PlatformMetricBar
          value={value}
          max={maxRequests}
          color={colorForPlatform(record.platform)}
        />
      ),
      sorter: (a, b) => a.requests - b.requests,
      defaultSortOrder: "descend",
    },
    {
      title: "Mentions",
      dataIndex: "mentions",
      key: "mentions",
      width: 260,
      render: (value: number, record) => (
        <PlatformMetricBar
          value={value}
          max={maxMentions}
          color={colorForPlatform(record.platform)}
          muted
        />
      ),
      sorter: (a, b) => a.mentions - b.mentions,
    },
  ];
}

function PlatformMetricBar({
  value,
  max,
  color,
  muted = false,
}: {
  readonly value: number;
  readonly max: number;
  readonly color: string;
  readonly muted?: boolean;
}) {
  const width = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="h-2 rounded-full flex-1 bg-grey-100 overflow-hidden min-w-[80px]">
        <div
          className="h-full rounded-full"
          style={{
            width: `${width}%`,
            background: muted ? `${color}AA` : color,
          }}
        />
      </div>
      <span className="text-xs tabular-nums font-medium text-content-primary min-w-[60px] text-right">
        {value.toLocaleString()}
      </span>
    </div>
  );
}

function getPeriodKeysDesc(usage: OrganizationResponse["usage"]): string[] {
  return Object.keys(usage).sort((a, b) => b.localeCompare(a));
}

interface TopBarProps {
  readonly organization: OrganizationResponse | undefined;
  readonly onLogout: () => void;
}

function TopBar({ organization, onLogout }: TopBarProps) {
  const initial = organization?.name?.[0]?.toUpperCase() ?? "?";
  return (
    <header className="sticky top-0 z-20 backdrop-blur bg-white/70 border-b border-arc-border">
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
        <Logo size="md" />
        <div className="flex items-center gap-3">
          {organization && (
            <div className="hidden sm:flex items-center gap-3 pr-3 border-r border-arc-border">
              <Avatar
                size={36}
                style={{ background: "#6366F1", fontWeight: 600 }}
              >
                {initial}
              </Avatar>
              <div className="leading-tight">
                <div className="text-sm font-semibold text-content-primary">
                  {organization.name}
                </div>
                <div className="text-xs text-content-tertiary capitalize">
                  {organization.status}
                </div>
              </div>
            </div>
          )}
          <Button
            icon={<LogoutOutlined />}
            onClick={onLogout}
            className="!rounded-arc-md"
          >
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </div>
    </header>
  );
}

interface OrganizationSummaryProps {
  readonly organization: OrganizationResponse;
}

function OrganizationSummary({ organization }: OrganizationSummaryProps) {
  const initial = organization.name?.[0]?.toUpperCase() ?? "?";
  return (
    <Card className="crow-card !border-0 mb-6" bordered={false}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Avatar
            size={56}
            style={{
              background: "#6366F1",
              fontWeight: 700,
              fontSize: 22,
            }}
          >
            {initial}
          </Avatar>
          <div>
            <Title level={3} className="!m-0">
              {organization.name}
            </Title>
            <Text type="secondary">{organization.id}</Text>
          </div>
        </div>
        <Space size={[8, 8]} wrap>
          <Tag
            color={organization.status === "active" ? "green" : "default"}
            className="!rounded-full !px-3 capitalize"
          >
            {organization.status}
          </Tag>
          <Tag className="!rounded-full !px-3 !bg-accent-soft !text-accent-dark !border-transparent">
            {organization.period}-day cycle
          </Tag>
        </Space>
      </div>
    </Card>
  );
}

interface CurrentPeriodProps {
  readonly data: OrganizationResponse;
}

function CurrentPeriod({ data }: CurrentPeriodProps) {
  const periodKeys = getPeriodKeysDesc(data.usage);
  const currentStart = periodKeys[0];
  const { total, platforms } = splitTotalAndPlatforms(data.current_usage);
  const maxRequests = Math.max(1, ...platforms.map((p) => p.requests));
  const maxMentions = Math.max(1, ...platforms.map((p) => p.mentions));

  return (
    <section className="mb-10">
      <div className="flex items-baseline justify-between mb-4">
        <Title level={4} className="!m-0">
          Current period
        </Title>
        <Text type="secondary" className="text-xs">
          Auto-refreshes every minute
        </Text>
      </div>

      {currentStart && (
        <div className="mb-5">
          <PeriodInfo periodStart={currentStart} periodDays={data.period} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        <UsageCard
          title="Requests"
          used={total.requests}
          limit={data.limits?.requests}
          color="#6366F1"
          tileClassName="tile-accent-indigo"
          icon={<ThunderboltOutlined />}
          subtitle="this billing period"
        />
        <UsageCard
          title="Mentions"
          used={total.mentions}
          limit={data.limits?.mentions}
          color="#EC4899"
          tileClassName="tile-accent-pink"
          icon={<MessageOutlined />}
          subtitle="this billing period"
        />
      </div>

      <Card
        className="crow-card !border-0"
        bordered={false}
        title={
          <div className="flex items-center gap-2">
            <BarChartOutlined className="text-accent" />
            <span>Breakdown by platform</span>
          </div>
        }
      >
        <Table<PlatformRow>
          columns={platformTableColumns(maxRequests, maxMentions)}
          dataSource={platforms}
          pagination={false}
          size="middle"
          locale={{ emptyText: "No activity yet in this period" }}
        />
      </Card>
    </section>
  );
}

interface HistoryProps {
  readonly data: OrganizationResponse;
}

function History({ data }: HistoryProps) {
  const periodKeys = getPeriodKeysDesc(data.usage);
  const pastKeys = periodKeys.slice(1);
  if (pastKeys.length === 0) {
    return null;
  }

  const items = pastKeys.map((key) => {
    const { total, platforms } = splitTotalAndPlatforms(data.usage[key]);
    const maxRequests = Math.max(1, ...platforms.map((p) => p.requests));
    const maxMentions = Math.max(1, ...platforms.map((p) => p.mentions));
    const start = dayjs(key);
    const end = start.add(data.period, "day");
    return {
      key,
      label: (
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <div>
            <Text className="!font-semibold !text-content-primary">
              {start.format("MMM D, YYYY")}
              <span className="mx-2 text-content-tertiary">→</span>
              {end.format("MMM D, YYYY")}
            </Text>
          </div>
          <Space size="small">
            <Tag
              icon={<ThunderboltOutlined />}
              className="!rounded-full !border-0 !bg-accent-soft !text-accent-dark"
            >
              {total.requests.toLocaleString()}
            </Tag>
            <Tag
              icon={<MessageOutlined />}
              color="magenta"
              className="!rounded-full"
            >
              {total.mentions.toLocaleString()}
            </Tag>
          </Space>
        </div>
      ),
      children: (
        <Table<PlatformRow>
          columns={platformTableColumns(maxRequests, maxMentions)}
          dataSource={platforms}
          pagination={false}
          size="small"
        />
      ),
    };
  });

  return (
    <section className="mb-10">
      <Title level={4} className="!mb-4">
        Previous periods
      </Title>
      <Collapse
        items={items}
        bordered={false}
        className="!bg-transparent"
        expandIconPosition="end"
      />
    </section>
  );
}

export function UsageDashboard() {
  const { data, isLoading, error } = useOrganization();
  const { logout } = useAuth();

  return (
    <div className="min-h-screen">
      <TopBar organization={data} onLogout={logout} />
      <main className="max-w-6xl mx-auto px-6 py-8 crow-fade-up">
        {isLoading ? (
          <div className="flex justify-center items-center min-h-[60vh]">
            <Spin size="large" />
          </div>
        ) : error || !data ? (
          <Result
            status="error"
            title="Failed to load usage data"
            subTitle="Please try again later."
          />
        ) : (
          <>
            <OrganizationSummary organization={data} />
            <CurrentPeriod data={data} />
            <History data={data} />
          </>
        )}
      </main>
    </div>
  );
}
